import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Copy, Trash2, CalendarClock, Users, Home, Inbox, Eye, Heart, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { SALE_STATUS_LABEL, SALE_STATUS_COLOR, LEAD_STATUS_LABEL } from "../../components/koop/koopUtils";

const STATUSES = ["concept", "actief", "onder_bod", "verkocht_ov", "verkocht", "ingetrokken"];
const LEAD_STATUSES = ["NEW", "CONTACTED", "APPOINTMENT", "QUALIFIED", "WON", "LOST"];
const QUALITY_COLOR = { HOT: "bg-red-100 text-red-700", WARM: "bg-amber-100 text-amber-700", COLD: "bg-slate-100 text-slate-500" };

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-border bg-white p-4 shadow-sm">
    <div className="text-2xl font-extrabold text-navy">{value}</div>
    <div className="text-xs font-semibold uppercase text-slate-400">{label}</div>
  </div>
);

const KoopMakelaar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("listings");
  const [dash, setDash] = useState(null);
  const [listings, setListings] = useState(null);
  const [leads, setLeads] = useState(null);
  const [leadFilter, setLeadFilter] = useState("");
  const [office, setOffice] = useState(null);
  const [slotsFor, setSlotsFor] = useState(null); // listing obj
  const [openLead, setOpenLead] = useState(null);

  const loadListings = () => api("/koop/makelaar/listings").then((d) => setListings(d.listings)).catch(() => setListings([]));
  const loadLeads = () => api(`/koop/makelaar/leads${leadFilter ? `?status=${leadFilter}` : ""}`).then((d) => setLeads(d.leads)).catch(() => setLeads([]));
  const loadOffice = () => api("/koop/makelaar/office").then(setOffice).catch(() => {});

  useEffect(() => { api("/koop/makelaar/dashboard").then(setDash).catch(() => {}); loadListings(); loadOffice(); }, []);
  useEffect(() => { loadLeads(); /* eslint-disable-next-line */ }, [leadFilter]);

  if (user && user.role !== "makelaar") {
    return <div className="mx-auto max-w-md px-4 py-20 text-center"><p className="text-slate-500">Deze pagina is alleen voor makelaar-accounts.</p></div>;
  }

  const setStatus = async (l, status) => {
    try { await api(`/koop/makelaar/listings/${l.id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }); loadListings(); }
    catch (e) { toast.error(e.message); }
  };
  const duplicate = async (l) => { try { await api(`/koop/makelaar/listings/${l.id}/duplicate`, { method: "POST" }); toast.success("Gedupliceerd"); loadListings(); } catch (e) { toast.error(e.message); } };
  const remove = async (l) => { if (!window.confirm("Woning verwijderen?")) return; try { await api(`/koop/makelaar/listings/${l.id}`, { method: "DELETE" }); loadListings(); } catch (e) { toast.error(e.message); } };

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button data-testid={`tab-${id}`} onClick={() => setTab(id)} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${tab === id ? "bg-brandblue text-white" : "bg-slate-100 text-slate-600"}`}><Icon size={16} /> {label}</button>
  );

  return (
    <div>
      <PageHeader icon={Home} title="Koopwoningen — Makelaar" subtitle={office?.office?.name}
        right={<Button data-testid="new-koop-listing" onClick={() => navigate("/koop/makelaar/wizard")} className="rounded-xl bg-white text-navy hover:bg-white/90"><Plus size={16} className="mr-2" /> Nieuwe woning</Button>} />

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {dash && (
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            <Stat label="Woningen" value={dash.total_listings} />
            <Stat label="Actief" value={dash.active_listings} />
            <Stat label="Views" value={dash.total_views} />
            <Stat label="Leads" value={dash.total_leads} />
            <Stat label="Nieuw" value={dash.new_leads} />
            <Stat label="Bezichtigingen" value={dash.total_bookings} />
          </div>
        )}

        <div className="mb-6 flex gap-2">
          <TabBtn id="listings" icon={Home} label="Mijn woningen" />
          <TabBtn id="leads" icon={Inbox} label="Lead inbox" />
          <TabBtn id="office" icon={Users} label="Kantoor" />
        </div>

        {tab === "listings" && (
          listings === null ? <Loader2 className="animate-spin text-brandblue" /> :
          listings.length === 0 ? <div className="rounded-3xl border border-dashed border-border p-16 text-center text-slate-500">Nog geen koopwoningen. Klik op "Nieuwe woning".</div> : (
            <div className="space-y-3" data-testid="broker-listings">
              {listings.map((l) => (
                <div key={l.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-white p-4 shadow-sm">
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-slate-100">{(l.image || l.gallery?.[0]) && <img src={l.image || l.gallery[0]} alt="" className="h-full w-full object-cover" />}</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-display font-bold text-navy">{l.title || `${l.property_type} in ${l.city}`}</div>
                    <div className="text-sm text-slate-500">{l.street}, {l.city} · € {Number(l.price || 0).toLocaleString("nl-NL")}</div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Eye size={12} /> {l.view_count || 0}</span>
                      <span className="inline-flex items-center gap-1"><Heart size={12} /> {l.favorite_count || 0}</span>
                      <span className="inline-flex items-center gap-1"><Inbox size={12} /> {l.lead_count || 0} leads</span>
                      <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> {l.booking_count || 0} bezicht.</span>
                    </div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${SALE_STATUS_COLOR[l.status]}`}>{SALE_STATUS_LABEL[l.status]}</span>
                  <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                    <SelectTrigger className="w-[150px] rounded-xl" data-testid={`status-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{SALE_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/koop/makelaar/wizard/${l.id}`)} className="rounded-lg" data-testid={`edit-${l.id}`}>Bewerk</Button>
                    <Button size="sm" variant="outline" onClick={() => setSlotsFor(l)} className="rounded-lg" data-testid={`slots-${l.id}`}><CalendarClock size={15} /></Button>
                    <Button size="sm" variant="outline" onClick={() => duplicate(l)} className="rounded-lg"><Copy size={15} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(l)} className="rounded-lg text-danger"><Trash2 size={15} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "leads" && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Select value={leadFilter || "all"} onValueChange={(v) => setLeadFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[200px] rounded-xl" data-testid="lead-filter"><SelectValue placeholder="Alle statussen" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Alle statussen</SelectItem>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{LEAD_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {leads === null ? <Loader2 className="animate-spin text-brandblue" /> :
            leads.length === 0 ? <div className="rounded-3xl border border-dashed border-border p-16 text-center text-slate-500">Nog geen leads.</div> : (
              <div className="overflow-x-auto rounded-2xl border border-border bg-white shadow-sm" data-testid="lead-inbox">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-400"><tr>
                    <th className="p-3">Datum</th><th className="p-3">Type</th><th className="p-3">Consument</th><th className="p-3">Woning</th><th className="p-3">Kwaliteit</th><th className="p-3">Status</th>
                  </tr></thead>
                  <tbody>
                    {leads.map((ld) => (
                      <tr key={ld.id} onClick={() => setOpenLead(ld)} data-testid={`lead-row-${ld.id}`} className="cursor-pointer border-t border-border hover:bg-slate-50">
                        <td className="p-3 text-slate-500">{(ld.created_at || "").slice(0, 10)}</td>
                        <td className="p-3 font-medium">{ld.lead_type}</td>
                        <td className="p-3">{ld.name}<div className="text-xs text-slate-400">{ld.email}</div></td>
                        <td className="p-3 text-slate-500">{ld.property_summary?.title || "—"}</td>
                        <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${QUALITY_COLOR[ld.quality] || ""}`}>{ld.quality}</span></td>
                        <td className="p-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold">{LEAD_STATUS_LABEL[ld.status]}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "office" && office && (
          <OfficePanel office={office} onChange={loadOffice} isAdmin={office.is_admin} />
        )}
      </div>

      {slotsFor && <SlotManager listing={slotsFor} onClose={() => { setSlotsFor(null); loadListings(); }} />}
      {openLead && <LeadDetail lead={openLead} members={office?.members || []} onClose={() => setOpenLead(null)} onChanged={() => { loadLeads(); }} />}
    </div>
  );
};

const SlotManager = ({ listing, onClose }) => {
  const [slots, setSlots] = useState(null);
  const [form, setForm] = useState({ date: "", start_time: "", end_time: "", slot_type: "individueel", capacity: 1, buffer_minutes: 0, duration_minutes: 20 });
  const load = () => api(`/koop/makelaar/listings/${listing.id}/slots`).then((d) => setSlots(d.slots)).catch(() => setSlots([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const add = async () => {
    if (!form.date || !form.start_time || !form.end_time) return toast.error("Vul datum en tijden in.");
    try { await api("/koop/makelaar/slots", { method: "POST", body: JSON.stringify({ ...form, listing_id: listing.id, capacity: Number(form.capacity) }) }); setForm({ ...form, start_time: "", end_time: "" }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const del = async (sid) => { try { await api(`/koop/makelaar/slots/${sid}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="slot-manager">
        <DialogHeader><DialogTitle className="font-display text-navy">Bezichtigingsmomenten</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Datum</Label><Input type="date" data-testid="slot-date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="mt-1 rounded-xl" /></div>
          <div><Label>Type</Label><Select value={form.slot_type} onValueChange={(v) => setForm((f) => ({ ...f, slot_type: v }))}><SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="individueel">Individueel</SelectItem><SelectItem value="open_huis">Open huis</SelectItem></SelectContent></Select></div>
          <div><Label>Van</Label><Input data-testid="slot-start" placeholder="14:00" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="mt-1 rounded-xl" /></div>
          <div><Label>Tot</Label><Input data-testid="slot-end" placeholder="14:20" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="mt-1 rounded-xl" /></div>
          <div><Label>Plekken</Label><Input type="number" value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))} className="mt-1 rounded-xl" /></div>
          <div className="flex items-end"><Button data-testid="add-slot" onClick={add} className="w-full rounded-xl bg-brandblue hover:bg-brandblue/90"><Plus size={16} className="mr-1" /> Toevoegen</Button></div>
        </div>
        <div className="mt-2 max-h-56 space-y-2 overflow-y-auto">
          {slots === null ? <Loader2 className="animate-spin text-brandblue" /> : slots.length === 0 ? <p className="text-sm text-slate-400">Nog geen momenten.</p> :
            slots.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-sm">
                <span>{s.date} · {s.start_time}–{s.end_time} {s.slot_type === "open_huis" ? "· Open huis" : ""} <span className="text-slate-400">({s.booked_count}/{s.capacity} geboekt)</span></span>
                <button onClick={() => del(s.id)} className="text-danger"><X size={15} /></button>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LeadDetail = ({ lead, members, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { api(`/koop/makelaar/leads/${lead.id}`).then(setDetail).catch(() => setDetail(lead)); }, [lead]);
  const setStatus = async (status) => { try { await api(`/koop/makelaar/leads/${lead.id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }); toast.success("Status bijgewerkt"); onChanged(); setDetail((d) => ({ ...d, status })); } catch (e) { toast.error(e.message); } };
  const assign = async (uid) => { try { await api(`/koop/makelaar/leads/${lead.id}/assign`, { method: "PATCH", body: JSON.stringify({ assigned_to: uid }) }); toast.success("Toegewezen"); setDetail((d) => ({ ...d, assigned_to: uid })); } catch (e) { toast.error(e.message); } };
  const addNote = async () => { if (!note.trim()) return; try { const n = await api(`/koop/makelaar/leads/${lead.id}/notes`, { method: "POST", body: JSON.stringify({ note }) }); setDetail((d) => ({ ...d, notes: [n, ...(d.notes || [])] })); setNote(""); } catch (e) { toast.error(e.message); } };
  const d = detail || lead;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="lead-detail">
        <DialogHeader><DialogTitle className="font-display text-navy">{d.name} · {d.lead_type}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-3"><span>📧 {d.email}</span><span>📞 {d.phone || "—"}</span></div>
          {d.property_summary && <div className="text-slate-500">Woning: {d.property_summary.title} — {d.property_summary.city}</div>}
          {d.qualification && Object.keys(d.qualification).length > 0 && (
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="mb-1 text-xs font-bold uppercase text-slate-400">Kwalificatie</div>
              {Object.entries(d.qualification).map(([k, v]) => <div key={k} className="flex justify-between text-xs"><span className="text-slate-500">{k}</span><span className="font-medium">{String(v)}</span></div>)}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Status</Label><Select value={d.status} onValueChange={setStatus}><SelectTrigger className="mt-1 rounded-xl" data-testid="lead-status-select"><SelectValue /></SelectTrigger><SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{LEAD_STATUS_LABEL[s]}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Toewijzen aan</Label><Select value={d.assigned_to || ""} onValueChange={assign}><SelectTrigger className="mt-1 rounded-xl" data-testid="lead-assign-select"><SelectValue placeholder="Medewerker" /></SelectTrigger><SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div>
            <Label className="text-xs">Interne notitie</Label>
            <div className="mt-1 flex gap-2"><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[40px] rounded-xl" data-testid="lead-note-input" /><Button onClick={addNote} data-testid="lead-note-add" className="rounded-xl bg-brandblue hover:bg-brandblue/90">+</Button></div>
            <div className="mt-2 space-y-1">{(d.notes || []).map((n) => <div key={n.id} className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs"><span className="text-slate-400">{(n.created_at || "").slice(0, 10)} · {n.author_name}</span><div>{n.note}</div></div>)}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const OfficePanel = ({ office, onChange, isAdmin }) => {
  const [email, setEmail] = useState("");
  const add = async () => { try { await api("/koop/makelaar/office/members", { method: "POST", body: JSON.stringify({ email }) }); toast.success("Medewerker toegevoegd"); setEmail(""); onChange(); } catch (e) { toast.error(e.message); } };
  const remove = async (id) => { try { await api(`/koop/makelaar/office/members/${id}`, { method: "DELETE" }); onChange(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="max-w-xl rounded-3xl border border-border bg-white p-6 shadow-sm" data-testid="office-panel">
      <h2 className="font-display text-lg font-bold text-navy">{office.office.name}</h2>
      <div className="mt-4 space-y-2">
        {office.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-sm">
            <span>{m.name || m.email} <span className="text-xs text-slate-400">· {m.office_role || "broker"}</span></span>
            {isAdmin && m.id !== office.office.admin_user_id && <button onClick={() => remove(m.id)} className="text-danger"><X size={15} /></button>}
          </div>
        ))}
      </div>
      {isAdmin && (
        <div className="mt-4 flex gap-2">
          <Input placeholder="e-mail medewerker (makelaar-account)" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl" data-testid="office-member-email" />
          <Button onClick={add} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="office-member-add"><Plus size={16} /></Button>
        </div>
      )}
    </div>
  );
};

export default KoopMakelaar;
